import { useCallback, useRef } from 'react';

import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { isInitial } from '@confluence/browser-metrics/entry-points/utils.ts';

const TTCC_END_MARK = 'browser-metric:ttcc.end';
const TTCC_MEASURE = 'browser-metric:ttcc';
const PAGE_LOAD_START_MARK = 'browser-metric.start';

type TimeToContentCompleteIdentity = {
	contentId: string;
	revision?: string;
};

type UseTimeToContentCompleteProps = TimeToContentCompleteIdentity & {
	enabled: boolean;
};

const createTimeToContentCompleteMeasure = ():
	| { duration: number; isInitialLoad: boolean }
	| undefined => {
	if (!window.performance?.mark || !window.performance?.measure) {
		return;
	}

	try {
		const isInitialLoad = isInitial();
		window.performance.mark(TTCC_END_MARK);
		const measure = window.performance.measure(
			TTCC_MEASURE,
			isInitialLoad ? undefined : PAGE_LOAD_START_MARK,
			TTCC_END_MARK,
		);
		return { duration: measure.duration, isInitialLoad };
	} catch {
		// Performance measurements must never interfere with rendering.
		return;
	}
};

/**
 * Returns a callback that records TTCC when progressive rendering commits its final content batch.
 * TTCC is standalone telemetry and intentionally does not change UFO page-load completion or TTAI.
 */
export const useTimeToContentComplete = ({
	contentId,
	enabled,
	revision,
}: UseTimeToContentCompleteProps): (() => void) | undefined => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const completedIdentityRef = useRef<TimeToContentCompleteIdentity | null>(null);

	const onContentComplete = useCallback(() => {
		if (!enabled) {
			return;
		}

		const completedIdentity = completedIdentityRef.current;
		if (completedIdentity?.contentId === contentId && completedIdentity.revision === revision) {
			return;
		}
		completedIdentityRef.current = { contentId, revision };

		const measurement = createTimeToContentCompleteMeasure();
		if (!measurement) {
			return;
		}

		const duration = Math.round(measurement.duration);
		addUFOCustomData({ ttcc: duration });

		createEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'measured',
				actionSubject: 'timeToContentComplete',
				source: 'viewPageScreen',
				attributes: {
					duration,
					isInitial: measurement.isInitialLoad,
				},
			},
		}).fire();
	}, [contentId, createEvent, enabled, revision]);

	return enabled ? onContentComplete : undefined;
};
